import { Backdrop, Box, Divider, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { VirtualItem } from "@tanstack/react-virtual";
import { API, graphqlOperation } from "aws-amplify";
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { WorkspacesContext } from "../../../contexts/WorkspacesContext";
import { onWorkspaceDashboardMembership } from "../../../graphql/subscriptions";
import useDashboardsTable from "../../../hooks/useDashboardsTable";
import useVirtualTable from "../../../hooks/useVirtualTable";
import useWorkspaces from "../../../hooks/useWorkspaces";
import { scrollbarStyle } from "../../../shared/dashboard";
import { Dashboard, Workspace } from "../../../types/files";
import ArrayUtils from "../../../utils/ArrayUtils";
import { collectionKey } from "../../organisms/dashboards/DashboardsView";
import { FallbackLoading } from "../../templates/loader";
import NoDashboardsFoundView from "../dashboards-empty/NoDashboardsFoundView";
import DashboardsTableFilter from "../dashboards-table/DashboardsTableFilter";
import DashboardsTableHeader from "../dashboards-table/DashboardsTableHeader";
import DashboardsTableRow from "../dashboards-table/DashboardsTableRow";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import UpgradeToProModal from "../../modals/investors/UpgradeToProModal";

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
    },
    scrollable: {
        width: '100%',
        height: '100%',
        marginBottom: '16px !important',
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    headers: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'smooth',
        ...scrollbarStyle,
    },
    //
    table: {
        width: '100%',
        height: 'calc(100vh - 180px)',
        marginLeft: 12,
    },
    header: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'auto',
        borderBottom: `1px solid ${theme.colors.neutral['400']}`,
        ...scrollbarStyle,
    },
    rows: {
        position: 'relative',
        minWidth: '100%',
        width: 'fit-content',
        height: '100%',
        overflow: 'auto',
        scrollBehavior: 'smooth',
        ...scrollbarStyle,
    },
    frostedRows: {
        position: 'absolute',
        top: 'unset !important',
        bottom: 'unset !important',
        left: 'unset !important',
        right: 'unset !important',
        width: 'calc(100vw - 155px)',
        height: 'calc(100vh - 220px)',
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
    },
    row: {
        position: 'absolute',
        top: 0,
        left: 0,
        minWidth: '100%',
        width: 'fit-content',
        height: 'auto',
        borderTop: `1px solid ${theme.colors.neutral['400']}`,
        background: `#fff`,
        "&:hover": {
            background: `rgb(246, 251, 251, 0.8)`,
        },
    },
    section: {
        width: '100%',
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 600,
        color: '#3ABDC6',
        "&::before, &::after": {
            borderTop: 'thin solid #3ABDC6',
        }
    },
    loadingText: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: 700,
        color: theme.colors.neutral['900'],
    },
    loadingTextSub: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 500,
        color: '#9e9e9e',
    },
    loading: {
        width: '100%',
        height: '100%',
        color: theme.palette.primary.main,
    },
    sharedTitle: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: '600',
        color: theme.colors.neutral['700'],
    },
}));

export const TableHeaders = ['Name', 'Fit score', 'Stage', 'Sectors', 'Location', 'Owner', 'Source', 'Request Intro', '[2]', 'Added', '[3]'];

const SharedByAll: React.FC<{
    workspace: Workspace,
    search?: string,
    withFilter?: boolean,
    flags?: any,
    onCount?: (count: number) => void,
}> = ({ workspace, search, withFilter, flags, onCount }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);
    const { setWorkspaceScores } = useContext(WorkspacesContext);
    const { isPublicView, externalDashboards, setExternalDashboards } = useContext(DashboardsContext);
    const { refreshScores, computeWorkspaceScores } = useWorkspaces();
    const { loading, rows, filters, handleFilterRows } = useDashboardsTable(workspace.id, search);
    const { allRows, rowVirtualizer, hasNextPage, virtualRowsRef, virtualHeaderRef }
        = useVirtualTable({ key: 'shared-by-all-131', source: rows, limit: 20, itemHeight: 60 });

    const [scoring, setScoring] = useState<boolean>(false);

    const workspaceDashboardMembershipSubscriptionRef = useRef<any>(null);
    const timerRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined);

    const columns = useMemo(() => TableHeaders.filter(column => !['Owner'].includes(column)), []);

    const getMembership = useCallback((dashboard: Dashboard) =>
        dashboard?.workspaceMemberships?.find(wsMem => wsMem.workspaceId === workspace?.id)
    , [workspace?.id]);

    const handleRefresh = useCallback(() => {
        if (!!workspace)
            refreshScores([workspace], externalDashboards, () => setScoring(true))
    }, [workspace, refreshScores, externalDashboards]);

    const updateSubscribedDashboards = useCallback((localDashboards: Dashboard[], dashboard?: Dashboard|null) => {
        if (!dashboard?.id)
            return;

        let newDashboards = [];

        if (!!localDashboards.find(d => d.id === dashboard?.id))
            newDashboards = localDashboards.map(d => d.id === dashboard?.id ? ({...dashboard}) : d);
        else
            newDashboards = [{...dashboard}, ...localDashboards];

        setExternalDashboards(ArrayUtils.sortByDescending(newDashboards, 'createdAt'));
        // eslint-disable-next-line
    }, []);

    const subscribeWorkspaceDashboardMembership = useCallback(async (localDashboards: Dashboard[]) => {
        if (workspaceDashboardMembershipSubscriptionRef.current)
            workspaceDashboardMembershipSubscriptionRef.current.unsubscribe();

        if (userGroup) {
            workspaceDashboardMembershipSubscriptionRef.current = await (API.graphql(
                graphqlOperation(onWorkspaceDashboardMembership, { group: userGroup })) as any)
                    .subscribe({ next: ({ value }: any) =>
                        updateSubscribedDashboards(localDashboards, value?.data?.onWorkspaceDashboardMembership)});
        }
    }, [userGroup, updateSubscribedDashboards]);

    useEffect(() => {
        subscribeWorkspaceDashboardMembership(externalDashboards);

        return () => {
            workspaceDashboardMembershipSubscriptionRef?.current?.unsubscribe();
        }
    }, [externalDashboards, subscribeWorkspaceDashboardMembership]);

    useEffect(() => {
        onCount?.(rows.length);
    // eslint-disable-next-line
    }, [rows]);

    useEffect(() => {
        if (!!workspace && scoring) {
            timerRef.current = setTimeout(() => {
                clearInterval(timerRef.current);
                computeWorkspaceScores([workspace]).then((updatedWorkspaceScores) => {
                    setWorkspaceScores(updatedWorkspaceScores);
                }).finally(() => setScoring(false));
            }, 3000);
        }

        return () => {
            clearTimeout(timerRef.current);
        }
    // eslint-disable-next-line
    }, [scoring]);

    if (!workspace)
        return (<></>);

    return (<>
        {loading ? (<FallbackLoading />) : (<>
            {isPublicView && (<>
                <Typography className={classes.sharedTitle} pl={1} py={1}>
                    {`Deals shared:`}
                </Typography>
                <Divider sx={{ width: '100%' }} />
            </>)}
            <Stack className={classes.table}>
                {loading ? (<FallbackLoading />) : (<>
                    {withFilter && ( <DashboardsTableFilter count={rows.length} /> )}
                    <Stack alignItems="flex-start" justifyContent="flex-start" height="100%">
                        <Box width="100%" height="fit-content" overflow="hidden">
                            <Box className={classes.header} ref={virtualHeaderRef}>
                                <DashboardsTableHeader
                                    columns={columns}
                                    filters={filters}
                                    onFilter={handleFilterRows}
                                    onRefreshScores={() => handleRefresh()}
                                    actions={['Remove from workspace']}
                                    xAdjust={80}
                                    noStatus multiAction />
                            </Box>
                        </Box>
                        {!!rows?.length ? (<>
                            <Box width="100%" height="100%" overflow="hidden">
                                <Backdrop open={!flags.sharedWorkspaces} className={classes.frostedRows} />
                                <Box className={classes.rows} ref={virtualRowsRef}
                                    sx={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                                    {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => (
                                        <Box className={classes.row}
                                            sx={{  height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)`, }}
                                            key={'shared-by-all-148-' + virtualRow.index} >
                                            {(virtualRow.index > allRows.length - 1) && hasNextPage
                                                ? (<FallbackLoading />)
                                                : (<DashboardsTableRow
                                                        dashboard={allRows[virtualRow.index]}
                                                        workspace={workspace ?? undefined}
                                                        columns={columns}
                                                        collectionKey={collectionKey}
                                                        timestamp={getMembership(allRows[virtualRow.index])?.updatedAt}
                                                        refreshingScore={scoring}
                                                        noStatus isPublic />)}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </>) : (
                            <Stack alignItems="flex-start" justifyContent="flex-start" width="100%" height="100%">
                                <Divider sx={{ width: '100%' }} />
                                <Stack className={classes.rows} alignItems="flex-start" justifyContent="flex-start">
                                    <NoDashboardsFoundView noCompaniesYet={!search && !rows?.length} noCompaniesFound />
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </>)}
            </Stack>
        </>)}
        {!flags.sharedWorkspaces && (
            <UpgradeToProModal
                isOpen={!loading && !!rows?.length}
                anchorElement={virtualHeaderRef.current} />
        )}
    </>);
}

export default memo(withLDConsumer()(SharedByAll));